import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Card, Progress } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      message.error('Invalid or missing reset token');
      navigate('/login');
    }
  }, [token, navigate]);

  const BrandIcon = () => (
    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
      <img 
        src="/logo.png" 
        alt="Logo" 
        style={{ 
          width: '80px', 
          height: '80px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto'
        }} 
      />
    </div>
  );

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 15;
    
    return Math.min(strength, 100);
  };

  const getStrengthColor = (strength: number): string => {
    if (strength < 40) return '#ff4d4f';
    if (strength < 80) return '#faad14';
    return '#52c41a';
  };

  const getStrengthText = (strength: number): string => {
    if (strength < 40) return 'Weak';
    if (strength < 80) return 'Medium';
    return 'Strong';
  };

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
  };

  const onFinish = async (values: ResetPasswordFormValues) => {
    if (!token) return;
    
    setLoading(true);
    try {
      await authAPI.resetPassword({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      setResetSuccess(true);
      message.success('Password reset successful!');
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';
      message.error(errorMessage, 5);
    } finally {
      setLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <div className="auth-page">
        <Card className="auth-card" bodyStyle={{ padding: '1.25rem' }}>
          <BrandIcon />
          
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <CheckCircleOutlined style={{ fontSize: '3rem', color: '#52c41a', marginBottom: '1.5rem' }} />
            <Title level={3} style={{ color: '#0d0f1c', marginBottom: '1rem', marginTop: 0 }}>
              Password Reset Successful
            </Title>
            <Text style={{ color: '#47569e', display: 'block', marginBottom: '2rem' }}>
              Your password has been successfully reset. You can now login with your new password.
            </Text>
            <Button 
              type="primary"
              onClick={() => navigate('/login')}
              className="login-btn"
            >
              Go to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Card className="auth-card" bodyStyle={{ padding: '1.25rem' }}>
        <BrandIcon />

        <Title level={2} className="page-title" style={{ textAlign: 'center', marginBottom: '0.25rem', marginTop: 0 }}>
          Reset Password
        </Title>
        
        <Text style={{ 
          color: '#47569e', 
          display: 'block', 
          textAlign: 'center', 
          marginBottom: '1rem',
          fontSize: '14px'
        }}>
          Please enter your new password below.
        </Text>

        <Form
          form={form}
          name="reset-password"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          size="large"
        >
          <Form.Item
            label={<span className="form-item-label">New Password</span>}
            name="password"
            rules={[
              { required: true, message: 'Please input your new password!' },
              { min: 8, message: 'Password must be at least 8 characters long!' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#47569e' }} />}
              placeholder="Enter your new password"
              onChange={onPasswordChange}
            />
          </Form.Item>

          <div className="password-strength">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <Text style={{ fontSize: '12px', color: '#47569e' }}>Password Strength</Text>
              <Text style={{ fontSize: '12px', color: getStrengthColor(passwordStrength) }}>
                {getStrengthText(passwordStrength)}
              </Text>
            </div>
            <Progress 
              percent={passwordStrength} 
              showInfo={false}
              strokeColor={getStrengthColor(passwordStrength)}
              trailColor="#e6e9f4"
              size="small"
            />
          </div>

          <Form.Item
            label={<span className="form-item-label">Confirm Password</span>}
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
            style={{ marginTop: '0.5rem' }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#47569e' }} />}
              placeholder="Confirm your new password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="login-btn"
            >
              Reset Password
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '0.25rem' }}>
          <Button 
            type="link" 
            onClick={() => navigate('/login')}
            style={{ color: '#607afb' }}
          >
            Back to Login
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
